import { Component, OnInit, AfterViewInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { SnackBarService } from 'src/app/common/components/snackbar/snackbar.service';
import { SnackBarData } from 'src/app/common/components/snackbar/model/snackbar.model';
import { ConfirmDialogComponent, ConfirmDialogModel } from 'src/app/common/components/confirm-dialog/confirm-dialog.component';
import { FilterListItemModel, SearchFilterComponent, SearchParam } from 'src/app/common/components/search-filter/search-filter.component';
import { RequestData, SearchSort } from 'src/app/common/models/request-data.model';
import { SaleItemBulkUpdateModel } from './model/sale-item-bulk-update.model';
import { SaleItemBulkUpdateService } from './sale-item-bulk-update.service';
import { SaleItemBulkUpdateExportDialogComponent } from './dialogs/sale-item-bulk-update-export-dialog/sale-item-bulk-update-export-dialog.component';
import { SaleItemBulkUpdateScheduleDialogComponent } from './dialogs/sale-item-bulk-update-schedule-dialog/sale-item-bulk-update-schedule-dialog.component';
import { SaleItemBulkUpdateImportDialogComponent } from './dialogs/sale-item-bulk-update-import-dialog/sale-item-bulk-update-import-dialog.component';
import moment from 'moment';
import { PermissionService } from 'src/app/auth/permission.service';

@Component({
  selector: 'app-sale-item-bulk-update',
  templateUrl: './sale-item-bulk-update.component.html',
  styleUrl: './sale-item-bulk-update.component.scss',
  standalone: false
})
export class SaleItemBulkUpdateComponent implements OnInit, AfterViewInit {
  @ViewChild('searchFilter') searchFilter!: SearchFilterComponent;
  @ViewChild(MatSort) sort!: MatSort;

  isLoading = false;
  update_schedule_at: string = '';
  update_type: number = 0;

  scheduledDateVal: moment.Moment | null = null;
  originalScheduleDate: string = '';
  isEditingScheduleDate = false;

  selectedExportType = 2; // Default to 2 (Price Change)

  minDate!: Date;

  pagingData = { length: 0, pageSize: 50, pageIndex: 0 };
  displayedColumns: string[] = ['sl', 'code', 'class', 'name'];
  dataSource = new MatTableDataSource<SaleItemBulkUpdateModel>([]);
  permission: any;

  filter: FilterListItemModel[] = [
    {
      title: "Code",
      column: "sale_item.code",
    },
    {
      title: "Name",
      column: "sale_item.name",
    }
  ];

  columnSortData: SearchSort[] = [];
  searchColumFilter: SearchParam = {};

  constructor(
    private service: SaleItemBulkUpdateService,
    public dialog: MatDialog,
    private snackBarService: SnackBarService,
    private changeDetector: ChangeDetectorRef,
    private permissionService: PermissionService
  ) {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    this.minDate = tomorrow;
  }

  ngOnInit(): void {
    this.permission = this.permissionService.getPagePermission('sale-item-bulk-update');
    this.columnSortData.push({ column: 'sale_item.code', order: 'asc' });
  }

  ngAfterViewInit(): void {
    this.loadScheduleAndData();
  }

  loadScheduleAndData(): void {
    this.isLoading = true;
    this.service.getScheduleDetails().subscribe({
      next: (response) => {
        if (response.status === 'SUCCESS' && response.data) {
          const schedule = response.data;
          if (schedule.publish_at) {
            this.update_schedule_at = schedule.publish_at;
            this.update_type = Number(schedule.update_type);
            this.scheduledDateVal = moment(schedule.publish_at);
            this.originalScheduleDate = schedule.publish_at;
            this.selectedExportType = this.update_type === 1 ? 1 : 2; // Lock template export type to what is staged
            this.updateDisplayedColumns();
            this.loadData();
          } else {
            this.update_schedule_at = '';
            this.update_type = 0;
            this.scheduledDateVal = null;
            this.originalScheduleDate = '';
            this.dataSource.data = [];
            this.pagingData.length = 0;
            this.isLoading = false;
            this.updateDisplayedColumns();
          }
        } else {
          this.isLoading = false;
        }
        this.changeDetector.detectChanges();
      },
      error: () => {
        this.isLoading = false;
        this.showError('Failed to fetch scheduled update details.');
      }
    });
  }

  loadData(): void {
    this.isLoading = true;
    const requestData = this.buildRequestData();
    this.service.getList(requestData).subscribe({
      next: (response) => {
        if (response.status === 'SUCCESS' && response.data) {
          this.dataSource.data = response.data.items || [];
          this.pagingData.length = response.data.total_count || 0;
        } else {
          this.showError(response.message || 'Failed to load staged updates.');
        }
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.showError('Connection error while fetching updates.');
      }
    });
  }

  buildRequestData(): RequestData {
    return {
      search: {
        filters: {
          simpleSearch: this.searchColumFilter.freeSearch || [],
          advSearch: this.searchColumFilter.advSearch || [],
          scope: {
            limit: this.pagingData.pageSize,
            offset: (this.pagingData.pageIndex * this.pagingData.pageSize),
          }
        },
        sort: this.columnSortData,
      }
    };
  }

  onePageEvent(event: any): void {
    this.pagingData.pageIndex = event.pageIndex;
    this.pagingData.pageSize = event.pageSize;
    this.loadData();
  }

  onFilterApplied(filter: any): void {
    this.searchColumFilter = filter;
    this.pagingData.pageIndex = 0;
    this.loadData();
  }

  sortData(event: any): void {
    this.columnSortData = [{ column: event.active, order: event.direction }];
    this.pagingData.pageIndex = 0;
    this.loadData();
  }

  onScheduleDateChanged(): void {
    // CSS and control updates handled reactively
  }

  updateDisplayedColumns(): void {
    if (this.update_type === 1) {
      this.displayedColumns = ['sl', 'code', 'class', 'name', 'new_name', 'new_print_name', 'new_short_name'];
    } else if (this.update_type === 2) {
      this.displayedColumns = ['sl', 'code', 'class', 'name', 'price', 'new_price'];
    } else if (this.update_type === 3) {
      this.displayedColumns = ['sl', 'code', 'class', 'name', 'is_to_be_deleted'];
    } else {
      this.displayedColumns = ['sl', 'code', 'class', 'name'];
    }
  }

  enableEditSchedule(): void {
    this.isEditingScheduleDate = true;
  }

  cancelEditSchedule(): void {
    this.isEditingScheduleDate = false;
    if (this.originalScheduleDate) {
      this.scheduledDateVal = moment(this.originalScheduleDate);
    }
  }

  saveScheduleDate(): void {
    if (!this.scheduledDateVal) {
      return;
    }
    const formattedDate = this.scheduledDateVal.format('YYYY-MM-DD');
    this.isLoading = true;
    this.service.updateScheduleDate(formattedDate).subscribe({
      next: (response) => {
        if (response.status === 'SUCCESS') {
          this.showSuccess('Publish date has been updated successfully.');
          this.isEditingScheduleDate = false;
          this.loadScheduleAndData();
        } else {
          this.showError(response.message || 'Failed to update schedule date.');
          this.isLoading = false;
        }
      },
      error: () => {
        this.isLoading = false;
        this.showError('Network error updating schedule date.');
      }
    });
  }

  discardStagingData(): void {
    const dialogData = new ConfirmDialogModel(
      "Confirm Discard",
      "This action will delete all scheduled sale item revisions you made and is irreversible. Are you sure you want to continue?"
    );
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      maxWidth: "400px",
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.isLoading = true;
        this.service.discard().subscribe({
          next: (response) => {
            if (response.status === 'SUCCESS') {
              this.showSuccess('Staged revisions discarded successfully.');
              this.loadScheduleAndData();
            } else {
              this.showError(response.message || 'Failed to discard staged updates.');
              this.isLoading = false;
            }
          },
          error: () => {
            this.isLoading = false;
            this.showError('Network error trying to discard data.');
          }
        });
      }
    });
  }

  publishStagingData(): void {
    const dialogData = new ConfirmDialogModel(
      "Confirm Publish",
      "Are you sure you want to publish these revisions now? This will apply the changes live immediately."
    );
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      maxWidth: "400px",
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.isLoading = true;
        this.service.publish().subscribe({
          next: (response) => {
            if (response.status === 'SUCCESS') {
              this.showSuccess(response.message || 'Staged revisions published live successfully.');
              this.loadScheduleAndData();
            } else {
              this.showError(response.message || 'Failed to publish staged updates.');
              this.isLoading = false;
            }
          },
          error: () => {
            this.isLoading = false;
            this.showError('Network error trying to publish data.');
          }
        });
      }
    });
  }

  openScheduleDialog(): void {
    const dialogRef = this.dialog.open(SaleItemBulkUpdateScheduleDialogComponent, {
      width: '400px',
      data: {
        currentDate: this.scheduledDateVal,
        minDate: this.minDate
      }
    });

    dialogRef.afterClosed().subscribe((selectedDate) => {
      if (selectedDate) {
        this.scheduledDateVal = selectedDate;
        this.saveScheduleDate();
      }
    });
  }

  openExportDialog(): void {
    const dialogRef = this.dialog.open(SaleItemBulkUpdateExportDialogComponent, {
      width: '400px',
      data: {
        selectedExportType: this.selectedExportType,
        update_schedule_at: this.update_schedule_at,
        update_type: this.update_type
      }
    });

    dialogRef.afterClosed().subscribe((selectedType) => {
      if (selectedType !== undefined && selectedType !== null) {
        this.selectedExportType = selectedType;
        this.confirmExport();
      }
    });
  }

  confirmExport(): void {
    this.isLoading = true;

    this.service.exportTemplate(this.selectedExportType).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;

        let filename = 'sale_item_price_change.xlsx';
        if (this.selectedExportType === 1) {
          filename = 'sale_item_name_change.xlsx';
        } else if (this.selectedExportType === 2) {
          filename = 'sale_item_price_change.xlsx';
        } else if (this.selectedExportType === 3) {
          filename = 'sale_item_delete.xlsx';
        }

        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.isLoading = false;
        this.showSuccess('Template exported successfully.');
      },
      error: () => {
        this.isLoading = false;
        this.showError('Failed to export template.');
      }
    });
  }

  openImportDialog(): void {
    const dialogRef = this.dialog.open(SaleItemBulkUpdateImportDialogComponent, {
      width: '450px',
      data: {
        update_schedule_at: this.update_schedule_at,
        minDate: this.minDate
      }
    });

    dialogRef.afterClosed().subscribe((imported) => {
      if (imported) {
        this.loadScheduleAndData();
      }
    });
  }

  discardSingleItem(rowItem: SaleItemBulkUpdateModel): void {
    const dialogData = new ConfirmDialogModel(
      "Exclude Item",
      `Are you sure you want to exclude ${rowItem.item_name} from this bulk update?`
    );
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      maxWidth: "400px",
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.isLoading = true;
        this.service.excludeItem(rowItem.sale_item_id).subscribe({
          next: (response) => {
            if (response.status === 'SUCCESS') {
              this.showSuccess('Item excluded successfully.');
              this.loadData();
            } else {
              this.showError(response.message || 'Failed to exclude item.');
              this.isLoading = false;
            }
          },
          error: () => {
            this.isLoading = false;
            this.showError('Network error trying to exclude item.');
          }
        });
      }
    });
  }



  private showSuccess(msg: string): void {
    const data: SnackBarData = {
      message: msg,
      title: 'Success',
      type: 'success'
    };
    this.snackBarService.openSnackBar(data);
  }

  private showError(msg: string): void {
    const data: SnackBarData = {
      message: msg,
      title: 'Failed',
      type: 'error'
    };
    this.snackBarService.openSnackBar(data);
  }
}
